/**
 * UPT-2096 (L2, web §5.1 et §5.2) — sélecteur de source de l'aperçu : onglets
 * (`role="tablist"`, flèches) à partir de 640 px, `<select>` natif en dessous
 * (affichage par CSS, aucune lecture de la largeur de fenêtre). Pur : ne reçoit
 * que des noms de fichiers et des rangs, jamais un document importé.
 */
import { useCallback, useRef, type KeyboardEvent } from 'react';
import { useIntl } from '@/react-intl';
import { prototypeSourceLabel } from './prototypeI18n';

export interface SourceSelectorSource {
  /** Rang de la source (0..7) ; null traité comme 0. */
  sourceIndex: number | null;
  fileName: string;
}

export interface SourceSelectorProps {
  sources: readonly SourceSelectorSource[];
  /** Position (0-based) de la source sélectionnée dans `sources`. */
  selected: number;
  onSelect: (position: number) => void;
}

export default function SourceSelector({ sources, selected, onSelect }: SourceSelectorProps) {
  const intl = useIntl();
  const t = useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl]
  );
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, position: number) => {
    let next = position;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (position + 1) % sources.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (position - 1 + sources.length) % sources.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = sources.length - 1;
    else return;
    event.preventDefault();
    onSelect(next);
    tabRefs.current[next]?.focus();
  };

  // eslint-disable-next-line no-restricted-syntax -- i18n-exempt: une seule source, aucun choix à proposer
  if (sources.length < 2) return null;
  const label = t('moduleStudio.prototype.sourceSelector.label');

  return (
    <div>
      <div role="tablist" aria-label={label} className="hidden flex-wrap gap-1 border-b border-portal-border sm:flex">
        {sources.map((source, position) => (
          <button
            key={`${source.sourceIndex ?? 0}-${source.fileName}`}
            ref={(element) => {
              tabRefs.current[position] = element;
            }}
            type="button"
            role="tab"
            aria-selected={selected === position}
            tabIndex={selected === position ? 0 : -1}
            className={`!rounded-none !border-0 !border-b-2 !px-3 !py-1 text-sm pointer-coarse:min-h-11 ${
              selected === position
                ? '!border-portal-accent text-portal-accent'
                : '!border-transparent text-portal-ink-muted'
            }`}
            onClick={() => onSelect(position)}
            onKeyDown={(event) => onKeyDown(event, position)}
          >
            <span dir="auto">{prototypeSourceLabel(t, source)}</span>
          </button>
        ))}
      </div>
      <select
        aria-label={label}
        value={selected}
        onChange={(event) => onSelect(Number(event.target.value))}
        className="w-full rounded-md border border-portal-border bg-portal-surface p-2 text-sm text-portal-ink pointer-coarse:min-h-11 sm:hidden"
      >
        {sources.map((source, position) => (
          <option key={`${source.sourceIndex ?? 0}-${source.fileName}`} value={position}>
            {prototypeSourceLabel(t, source)}
          </option>
        ))}
      </select>
    </div>
  );
}
